import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, RefreshCcw, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  cloudSourceService,
  SharePointSiteOption,
} from '@/services/cloudSourceService';
import { logger } from '@/utils/logger';
import { getFriendlyErrorMessage } from '@/utils/errorMessage';

interface SharePointSitePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Backend source row name (KB Cloud Folder Source.name). */
  sourceName: string;
  /** Existing site URL on the source, if any - used to highlight the current pick. */
  currentSiteUrl?: string | null;
  /** Called after the user saves a new site selection (so the caller can refresh). */
  onSaved: () => void | Promise<void>;
  /** Optional title override - defaults to "Pick a SharePoint site". */
  title?: string;
  /** Optional description override. */
  description?: string;
  /**
   * Mandatory mode (used right after first connect): no Cancel button, no
   * close (X), and outside-click / Escape won't dismiss. A freshly-connected
   * SharePoint source is unusable until a site is picked, so the user must
   * choose one (or delete the source). "Change site" leaves this false so the
   * user can back out without changing anything.
   */
  mandatory?: boolean;
}

/**
 * Shared dialog that fetches the SharePoint sites the user can access
 * (via Graph `/sites?search=*`, constrained by the user's SharePoint
 * ACLs at the Microsoft end) and lets them pick one, persisting the
 * chosen webUrl onto the source row. Used both immediately after OAuth
 * (initial site selection) and from the per-source card ("Change
 * site").
 *
 * Empty list = the user genuinely has no SharePoint sites they can read.
 * Copy points them to ask their IT admin for access.
 */
const SharePointSitePickerDialog: React.FC<SharePointSitePickerDialogProps> = ({
  open,
  onOpenChange,
  sourceName,
  currentSiteUrl,
  onSaved,
  title,
  description,
  mandatory = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState<SharePointSiteOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pickedSiteUrl, setPickedSiteUrl] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState<string>('');

  const fetchSites = async () => {
    setLoading(true);
    setError(null);
    setSites([]);
    try {
      const res = await cloudSourceService.listSharePointSites(sourceName);
      if (res.status === 'ok') {
        setSites(res.sites || []);
        if (!res.sites || res.sites.length === 0) {
          setError(
            "No SharePoint sites are accessible to your account. Ask your IT admin to grant you access to at least one site, then hit Refresh.",
          );
        }
      } else {
        setError(res.message || 'Could not load your SharePoint sites.');
      }
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Could not load your SharePoint sites.');
      logger.error('Failed to load SharePoint sites', err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch each time the dialog opens. Pre-select the source's existing
  // site so "Save site" is clearly a no-op until the user picks a new one.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setPickedSiteUrl(currentSiteUrl || '');
    fetchSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceName]);

  // Restrict to actual collaboration sites (paths under /sites/...) - hide
  // the tenant root ("/") and any built-in shells the user can't meaningfully
  // target as a sync root. This is also the denominator the counter shows.
  const sitesOnly = useMemo(
    () =>
      sites.filter((s) => {
        const path = (s.web_url || '').replace(/^https?:\/\/[^/]+/i, '');
        return path.toLowerCase().startsWith('/sites/');
      }),
    [sites],
  );

  const filteredSites = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sitesOnly;
    return sitesOnly.filter((s) => {
      const hay = [s.display_name, s.name, s.web_url]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [search, sitesOnly]);

  const handleSave = async () => {
    if (!pickedSiteUrl) {
      toast.error('Pick a site first.');
      return;
    }
    if (pickedSiteUrl === (currentSiteUrl || '')) {
      // No-op - close cleanly.
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      // Persist the human-friendly site title alongside the URL so the source
      // card can show "Orgain Demo Site" instead of just the URL slug. The
      // display name is only available here in the picker - it isn't derivable
      // from the webUrl - so capture it at selection time.
      const pickedSite = sites.find((s) => s.web_url === pickedSiteUrl);
      await cloudSourceService.saveSource({
        source: sourceName,
        sharepoint_site_url: pickedSiteUrl,
        sharepoint_site_name:
          pickedSite?.display_name || pickedSite?.name || undefined,
      });
      toast.success('SharePoint site saved.');
      onOpenChange(false);
      await onSaved();
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Failed to save site.');
      logger.error('Failed to save site URL', err);
      toast.error('Could not save site', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // In mandatory mode, ignore Radix-initiated closes (outside click /
        // Escape / X) - only an explicit Save closes the dialog. handleSave
        // calls the onOpenChange prop directly, bypassing this guard.
        if (!o && !saving && !mandatory) onOpenChange(false);
      }}
    >
      <DialogContent
        className="dark bg-background text-foreground sm:max-w-md"
        hideCloseButton={mandatory}
        onInteractOutside={mandatory ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={mandatory ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle>{title || 'Pick a SharePoint site'}</DialogTitle>
          <DialogDescription>
            {description ||
              "These are the SharePoint sites your Microsoft account can access. Orgain will only see content in the site you pick."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading your sites…
            </div>
          ) : error ? (
            <div className="text-sm text-destructive">{error}</div>
          ) : sites.length === 0 ? (
            <div className="text-sm text-muted-foreground">No sites found.</div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search sites by name or URL…"
                  className="pl-8"
                  autoFocus
                />
              </div>
              <div className="text-xs text-muted-foreground">
                {filteredSites.length} of {sitesOnly.length} site
                {sitesOnly.length === 1 ? '' : 's'}
                {search ? ` matching "${search}"` : ''}
              </div>
              <div className="max-h-72 overflow-y-auto rounded-md border">
                {filteredSites.length === 0 ? (
                  <div className="p-3 text-sm text-muted-foreground">
                    No sites match "{search}".
                  </div>
                ) : (
                  filteredSites.map((s) => {
                    if (!s.web_url) return null;
                    const isPicked = pickedSiteUrl === s.web_url;
                    const isCurrent = (currentSiteUrl || '') === s.web_url;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setPickedSiteUrl(s.web_url!)}
                        className={
                          'flex w-full flex-col items-start gap-0.5 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-accent ' +
                          (isPicked ? 'bg-accent' : '')
                        }
                      >
                        <span className="flex items-center gap-2 font-medium">
                          {s.display_name || s.name || s.web_url}
                          {isCurrent && (
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                              Current
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground break-all">
                          {/* Hide the tenant base URL - only the path under
                              the tenant matters here. Root site (no path)
                              would otherwise render as an empty string, so
                              fall back to "/" for it. */}
                          {(() => {
                            const path = (s.web_url || '').replace(
                              /^https?:\/\/[^/]+/i,
                              '',
                            );
                            return path || '/';
                          })()}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={fetchSites}
            disabled={loading || saving}
            size="sm"
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <div className="flex-1" />
          {!mandatory && (
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={saving || loading || !pickedSiteUrl}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {/* First pick (no site yet) → "Use this site"; swapping an existing
                site → "Save site". "Save" reads oddly when nothing's saved yet. */}
            {currentSiteUrl ? 'Save site' : 'Use this site'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SharePointSitePickerDialog;
